import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { AppNav } from "@/components/AppNav";
import { SITE_URL } from "@/lib/seo-jsonld";

export const Route = createFileRoute("/planos")({
  head: () => {
    const url = `${SITE_URL}/planos/`;
    const title = "Planos — Busca Blindados | Alertas de carros blindados";
    const description =
      "Busca e análise grátis. Alertas automáticos no piloto automático por R$29/mês — cancele quando quiser.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PlanosPage,
});

type Feature = {
  label: string;
  free: boolean | string;
  pro: boolean | string;
};

const FEATURES: Feature[] = [
  { label: "Buscar e ver todos os anúncios",  free: true,    pro: true    },
  { label: "Clicar direto no anunciante",     free: true,    pro: true    },
  { label: "Comparador de anúncios",          free: true,    pro: true    },
  { label: "Histórico de preço",              free: true,    pro: true    },
  { label: "Favoritos",                       free: "login", pro: true    },
  { label: "1 alerta inteligente",            free: "login", pro: true    },
  { label: "Até 4 alertas automáticos",        free: false,   pro: true    },
  { label: "Até 3 modelos por alerta",        free: false,   pro: true    },
];

function Cell({ val, highlight }: { val: boolean | string; highlight?: boolean }) {
  if (val === false) return <X className="mx-auto h-4 w-4 text-muted-foreground/30" />;
  if (val === true) return <Check className="mx-auto h-4 w-4 text-primary" />;
  if (val === "login") {
    return (
      <span className="text-xs text-muted-foreground">com login</span>
    );
  }
  return (
    <span className={`text-xs font-medium ${highlight ? "text-primary" : "text-foreground/80"}`}>
      {val}
    </span>
  );
}

function PlanosPage() {
  const { session } = useSession();
  const { profile, isPaid, isExpired } = useProfile();
  const isFree = profile?.tier === "free" && !isPaid;

  return (
    <div className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-5xl" />

      <main className="mx-auto max-w-5xl px-6 pb-32 pt-12">
        <div className="text-center">
          <h1 className="font-display text-4xl text-foreground sm:text-5xl">
            Busca grátis. Alertas no piloto automático.
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            Explore e compare à vontade — sem cadastro. Assine o Pro para 4 alertas monitorando por você.
          </p>
        </div>

        {isPaid && profile?.plan && (
          <div className="mt-8 rounded-xl border border-primary/30 bg-primary/10 px-5 py-4 text-center text-sm text-foreground">
            Você já tem o{" "}
            <span className="font-medium">{profile.plan.display_name}</span>.{" "}
            {profile.plan_expires_at && (
              <span className="text-muted-foreground">
                Válido até{" "}
                {new Date(profile.plan_expires_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
                .
              </span>
            )}
          </div>
        )}

        {isFree && (
          <div className="mt-8 rounded-xl border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-5 py-4 text-center text-sm text-muted-foreground">
            Você está no plano gratuito.{" "}
            <span className="text-foreground">Assine o Pro por R$29/mês para 4 alertas no piloto automático.</span>
          </div>
        )}

        {/* Price cards */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto">
          {/* Free */}
          <div className="surface-card rounded-3xl p-8">
            <h2 className="font-display text-2xl text-foreground">Grátis</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Para explorar o mercado.<br />Sem cartão de crédito.
            </p>
            <p className="mt-4 font-display text-3xl text-foreground">R$ 0</p>
            <div className="mt-8">
              {isFree ? (
                <Link
                  to="/alertas"
                  className="block w-full rounded-full border dark:border-white/20 border-black/20 dark:bg-white/[0.05] bg-black/[0.05] py-3 text-center text-sm font-medium text-foreground transition dark:hover:bg-white/10 hover:bg-black/10"
                >
                  Continuar grátis
                </Link>
              ) : session ? (
                <div className="w-full rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] py-3 text-center text-sm text-muted-foreground">
                  Incluído no cadastro
                </div>
              ) : (
                <Link
                  to="/login"
                  className="block w-full rounded-full border dark:border-white/20 border-black/20 dark:bg-white/[0.05] bg-black/[0.05] py-3 text-center text-sm font-medium text-foreground transition dark:hover:bg-white/10 hover:bg-black/10"
                >
                  Criar conta grátis
                </Link>
              )}
            </div>
          </div>

          {/* Pro */}
          <div className="surface-card relative rounded-3xl p-8 border border-primary/30">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-background">
              Piloto automático
            </span>
            <h2 className="font-display text-2xl text-foreground">Pro</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              4 alertas no piloto automático.<br />Cancele no dia que fechar negócio.
            </p>
            <p className="mt-4 font-display text-3xl text-foreground">
              R$ 29<span className="text-lg font-sans font-normal text-muted-foreground">/mês</span>
            </p>
            <div className="mt-8">
              {isPaid && !isExpired ? (
                <div className="w-full rounded-full border border-primary/40 bg-primary/10 py-3 text-center text-sm font-medium text-primary">
                  Este é seu plano
                </div>
              ) : (
                <Link
                  to="/upgrade"
                  search={{ plan: "pro" }}
                  preload={false}
                  className="block w-full rounded-full py-3 text-center text-sm font-medium transition bg-primary text-primary-foreground hover:opacity-90"
                >
                  {isExpired ? "Renovar" : session ? "Assinar" : "Começar"}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mt-12 overflow-x-auto rounded-2xl border dark:border-white/10 border-black/10">
          <table className="w-full min-w-[400px] text-sm">
            <thead>
              <tr className="border-b dark:border-white/10 border-black/10 dark:bg-white/[0.02] bg-black/[0.02]">
                <th className="px-5 py-3.5 text-left text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Recurso
                </th>
                <th className="w-28 px-4 py-3.5 text-center text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Grátis
                </th>
                <th className="w-28 px-4 py-3.5 text-center text-[10px] font-medium uppercase tracking-[0.16em] text-primary">
                  Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f) => (
                <tr key={f.label} className="border-b dark:border-white/5 border-black/5 last:border-0">
                  <td className="px-5 py-3 text-left text-xs text-foreground/80">{f.label}</td>
                  <td className="px-4 py-3 text-center"><Cell val={f.free} /></td>
                  <td className="px-4 py-3 text-center"><Cell val={f.pro} highlight /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </main>
    </div>
  );
}
